import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  output,
  OutputEmitterRef,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { selectInbox } from '@app/_shared/widgets/messages/_store/inbox/inbox.selectors';
import { MessageComponent } from '@app/_shared/widgets/messages/message/message.component';
import { InboxMessage } from '@core/models/interfaces/storage-data/inbox';
import { MixpanelService } from '@core/services/tracking/mixpanel.service';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-messages',
  templateUrl: './messages.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'messages',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, MessageComponent],
})
export class MessagesComponent implements OnDestroy {
  private store = inject(Store);
  private mixpanel = inject(MixpanelService);
  private _overlay = inject(Overlay);
  private _viewContainerRef = inject(ViewContainerRef);
  private _destroyRef = inject(DestroyRef);

  public open: OutputEmitterRef<boolean> = output();

  private _overlayRef!: OverlayRef;

  // R82. Signal queries, with the guard below rewritten deliberately: unconverted it would read a
  // function, always truthy, and the panel would attach a template that may not exist yet.
  private readonly _messagesOrigin = viewChild<MatButton>('messagesOrigin');
  private readonly _messagesPanel =
    viewChild<TemplateRef<unknown>>('messagesPanel');

  messages: WritableSignal<InboxMessage[]> = signal([]);
  unreadCount: WritableSignal<number> = signal(0);

  constructor() {
    this.store
      .select(selectInbox)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((collection) => {
        this.messages.set(collection);
        setTimeout(() => {
          this._calculateUnreadCount(collection);
        });
      });
  }

  ngOnDestroy(): void {
    // Dispose the overlay
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------
  openPanel(): void {
    this.mixpanel.track('Inbox Button click');
    // Return if the messages panel or its origin is not defined
    if (!this._messagesPanel() || !this._messagesOrigin()) {
      return;
    }

    this.open.emit(true);

    // Create the overlay if it doesn't exist
    if (!this._overlayRef) {
      this._createOverlay();
    }

    // Attach the portal to the overlay
    const messagesPanel = this._messagesPanel();
    // A1.4: crash-path only — the panel template exists once the view is up
    if (!messagesPanel) return;

    this._overlayRef.attach(
      new TemplatePortal(messagesPanel, this._viewContainerRef)
    );

    this.mixpanel.track('Inbox pop-up displayed');
  }

  closePanel(): void {
    this.open.emit(false);
    this._overlayRef.detach();

    this.mixpanel.track('Inbox pop-up button close');
  }

  trackByFn(index: number, item: InboxMessage): string | number {
    return item.id || index;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------
  private _createOverlay(): void {
    // Create the overlay
    this._overlayRef = this._overlay.create({
      hasBackdrop: true,
      // backdropClass: 'fuse-backdrop-on-mobile',
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .global()
        .centerHorizontally()
        .centerVertically(),
    });

    // Detach the overlay from the portal on backdrop click
    this._overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.closePanel();
      });
  }

  private _calculateUnreadCount(collection: InboxMessage[]): void {
    let count = 0;

    if (collection && collection.length) {
      count = collection.filter((message) => !message.isRead).length;
    }

    this.unreadCount.set(count);
  }
}
